// src/shared/utils/googleMaps.ts
import { Loader } from "@googlemaps/js-api-loader";

let loaderInstance: Loader | null = null;
let loadPromise: Promise<typeof google> | null = null;

const getLoader = () => {
  if (!loaderInstance) {
    loaderInstance = new Loader({
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      version: "weekly",
    });
  }
  return loaderInstance;
};

export const loadGoogleMapsApi = async (): Promise<typeof google> => {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    const loader = getLoader();

    // Import required libraries - this properly loads them onto the google object
    await Promise.all([
      loader.importLibrary('places'),
      loader.importLibrary('geocoding'),
    ]);

    // Return the global google object with all libraries attached
    return window.google;
  })();

  return loadPromise;
};

export interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface GeocodingResult {
  address_components: AddressComponent[];
  formatted_address: string;
  place_id: string;
}

export interface PostalCodeValidationResult {
  isValid: boolean;
  suggestions?: {
    postalCode?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  errorMessage?: string;
}

/**
 * Validates a postal code against the selected country, state, and city
 * using Google Geocoding API
 */
export const validatePostalCode = async (
  postalCode: string,
  country?: string,
  state?: string,
  city?: string
): Promise<PostalCodeValidationResult> => {
  if (!postalCode) {
    return { isValid: true };
  }

  try {
    const google = await loadGoogleMapsApi();
    const geocoder = new google.maps.Geocoder();

    // Build address query
    const addressParts = [postalCode];
    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (country) addressParts.push(country);

    const address = addressParts.join(', ');

    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
          const result = results[0];

          // Extract components from result
          let foundCity = '';
          let foundCountry = '';

          result.address_components?.forEach((component) => {
            if (component.types.includes('locality') || component.types.includes('postal_town')) {
              foundCity = component.long_name;
            }
            if (component.types.includes('country')) {
              foundCountry = component.long_name;
            }
          });

          // Normalize comparison function for case-insensitive and whitespace-tolerant matching
          const normalize = (str: string) =>
            str.trim().toLowerCase()
              .normalize('NFD') // Decompose characters
              .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
              .replace(/\s+/g, ' '); // Normalize whitespace

          let isValid = true;

          // Check if country matches (required if country is provided)
          if (country && foundCountry) {
            const normalizedFoundCountry = normalize(foundCountry);
            const normalizedCountry = normalize(country);

            if (normalizedFoundCountry !== normalizedCountry &&
                !normalizedFoundCountry.includes(normalizedCountry) &&
                !normalizedCountry.includes(normalizedFoundCountry)) {
              isValid = false;
            }
          }

          // Check if city matches (required if city is provided)
          if (isValid && city && foundCity) {
            const normalizedFoundCity = normalize(foundCity);
            const normalizedCity = normalize(city);

            // Check both ways: exact match or partial match
            if (normalizedFoundCity !== normalizedCity &&
                !normalizedFoundCity.includes(normalizedCity) &&
                !normalizedCity.includes(normalizedFoundCity)) {
              isValid = false;
            }
          }

          if (!isValid) {
            resolve({
              isValid: false,
              errorMessage: 'Postal code is not right',
            });
          } else {
            resolve({ isValid: true });
          }
        } else {
          // If geocoding fails, we can't validate - return valid to not block user
          resolve({
            isValid: true,
            errorMessage: 'Could not validate postal code',
          });
        }
      });
    });
  } catch {
    // If API fails, return valid to not block user
    return {
      isValid: true,
      errorMessage: 'Postal code validation unavailable',
    };
  }
};

/**
 * Gets the ISO country code from a country name using Google Geocoding API
 */
export const getCountryISOCode = async (countryName: string): Promise<string | null> => {
  if (!countryName) return null;

  try {
    const google = await loadGoogleMapsApi();
    const geocoder = new google.maps.Geocoder();

    return new Promise((resolve) => {
      geocoder.geocode(
        {
          address: countryName,
          componentRestrictions: { country: countryName },
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
            const countryComponent = results[0].address_components?.find((component) =>
              component.types.includes('country')
            );
            resolve(countryComponent?.short_name || null);
          } else {
            resolve(null);
          }
        }
      );
    });
  } catch {
    return null;
  }
};

export interface LocationValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates that state belongs to the selected country
 */
export const validateState = async (
  state: string,
  country: string
): Promise<LocationValidationResult> => {
  if (!state || !country) {
    return { isValid: true };
  }

  try {
    const google = await loadGoogleMapsApi();
    const geocoder = new google.maps.Geocoder();
    const countryISO = await getCountryISOCode(country);

    if (!countryISO) {
      return { isValid: true }; // Can't validate without country ISO
    }

    return new Promise((resolve) => {
      geocoder.geocode(
        {
          address: `${state}, ${country}`,
          componentRestrictions: { country: countryISO },
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
            const result = results[0];

            // Normalize function that handles special characters
            const normalize = (str: string) =>
              str.trim().toLowerCase()
                .normalize('NFD') // Decompose characters
                .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics

            const normalizedState = normalize(state);

            // Check if result contains both the state and country
            const hasState = result.address_components?.some((c) => {
              if (!c.types.includes('administrative_area_level_1')) {
                return false;
              }
              const normalizedComponentName = normalize(c.long_name);
              // Check both ways: state contains component or component contains state
              return normalizedComponentName.includes(normalizedState) ||
                     normalizedState.includes(normalizedComponentName);
            });

            const hasCountry = result.address_components?.some((c) =>
              c.types.includes('country') &&
              normalize(c.long_name) === normalize(country)
            );

            if (hasState && hasCountry) {
              resolve({ isValid: true });
            } else {
              resolve({
                isValid: false,
                errorMessage: 'State does not belong to the selected country',
              });
            }
          } else {
            resolve({
              isValid: false,
              errorMessage: 'State does not belong to the selected country',
            });
          }
        }
      );
    });
  } catch {
    return { isValid: true }; // Don't block on API errors
  }
};

/**
 * Validates that city belongs to the selected country and state
 */
/**
 * Geocodes an address string to get coordinates and address components
 * Used as fallback when user types address manually instead of selecting from dropdown
 */
export interface GeocodeResult {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  addressComponents: {
    country: string;
    state: string;
    city: string;
    postalCode: string;
    streetAddress: string;
  };
}

export const geocodeAddress = async (address: string): Promise<GeocodeResult | null> => {
  if (!address || address.trim().length < 5) {
    return null;
  }

  try {
    const google = await loadGoogleMapsApi();
    const geocoder = new google.maps.Geocoder();

    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
          const result = results[0];

          let countryName = '';
          let stateName = '';
          let cityName = '';
          let postalCode = '';
          let streetAddress = '';

          result.address_components?.forEach((component) => {
            if (component.types.includes('country')) {
              countryName = component.long_name;
            }
            if (component.types.includes('administrative_area_level_1') ||
                component.types.includes('administrative_area_level_2')) {
              if (!stateName) stateName = component.long_name;
            }
            if (component.types.includes('locality') ||
                component.types.includes('postal_town') ||
                component.types.includes('administrative_area_level_3') ||
                component.types.includes('sublocality')) {
              if (!cityName) cityName = component.long_name;
            }
            if (component.types.includes('postal_code')) {
              postalCode = component.long_name;
            }
            if (component.types.includes('street_number') || component.types.includes('route')) {
              streetAddress += (streetAddress ? ' ' : '') + component.long_name;
            }
          });

          const location = result.geometry?.location;
          if (location) {
            resolve({
              latitude: location.lat(),
              longitude: location.lng(),
              formattedAddress: result.formatted_address || address,
              addressComponents: {
                country: countryName,
                state: stateName,
                city: cityName,
                postalCode,
                streetAddress,
              },
            });
          } else {
            resolve(null);
          }
        } else {
          resolve(null);
        }
      });
    });
  } catch {
    return null;
  }
};

export const validateCity = async (
  city: string,
  country: string,
  state?: string
): Promise<LocationValidationResult> => {
  if (!city || !country) {
    return { isValid: true };
  }

  try {
    const google = await loadGoogleMapsApi();
    const geocoder = new google.maps.Geocoder();
    const countryISO = await getCountryISOCode(country);

    if (!countryISO) {
      return { isValid: true }; // Can't validate without country ISO
    }

    const addressParts = [city];
    if (state && state.trim()) addressParts.push(state);
    addressParts.push(country);

    return new Promise((resolve) => {
      geocoder.geocode(
        {
          address: addressParts.join(', '),
          componentRestrictions: { country: countryISO },
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
            const result = results[0];

            // Normalize function that handles special characters
            const normalize = (str: string) =>
              str.trim().toLowerCase()
                .normalize('NFD') // Decompose characters
                .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics

            const normalizedCity = normalize(city);

            // Check country first - this is required
            const hasCountry = result.address_components?.some((c) =>
              c.types.includes('country') &&
              normalize(c.long_name) === normalize(country)
            );

            if (!hasCountry) {
              resolve({
                isValid: false,
                errorMessage: 'City does not belong to the selected country/state',
              });
              return;
            }

            // Check if ANY city-type component exists in the result
            // We just want to ensure the geocoding returned SOME city, not necessarily exact match
            const hasAnyCityComponent = result.address_components?.some((c) =>
              c.types.includes('locality') ||
              c.types.includes('administrative_area_level_3') ||
              c.types.includes('postal_town') ||
              c.types.includes('sublocality') ||
              c.types.includes('administrative_area_level_2')
            );

            // If there's no city component at all, it's probably not a valid city
            if (!hasAnyCityComponent) {
              resolve({
                isValid: false,
                errorMessage: 'City does not belong to the selected country/state',
              });
              return;
            }

            // Now do a lenient check to see if the city name is similar
            const hasCity = result.address_components?.some((c) => {
              const isCityType = c.types.includes('locality') ||
                                 c.types.includes('administrative_area_level_3') ||
                                 c.types.includes('postal_town') ||
                                 c.types.includes('sublocality') ||
                                 c.types.includes('administrative_area_level_2');

              if (!isCityType) return false;

              const normalizedComponentName = normalize(c.long_name);

              // Very lenient matching - check if either contains the other
              return normalizedComponentName.includes(normalizedCity) ||
                     normalizedCity.includes(normalizedComponentName);
            });

            // Only validate state if it's provided and not empty
            let hasMatchingState = true; // Default to true if no state
            if (state && state.trim()) {
              const normalizedState = normalize(state);
              hasMatchingState = result.address_components?.some((c) =>
                c.types.includes('administrative_area_level_1') &&
                (normalize(c.long_name).includes(normalizedState) ||
                 normalizedState.includes(normalize(c.long_name)))
              ) || false;
            }

            if (hasCity && hasMatchingState) {
              resolve({ isValid: true });
            } else {
              resolve({
                isValid: false,
                errorMessage: 'City does not belong to the selected country/state',
              });
            }
          } else {
            // If geocoding fails or returns no results, allow it
            // This prevents blocking legitimate cities that Google might not recognize
            resolve({ isValid: true });
          }
        }
      );
    });
  } catch {
    return { isValid: true }; // Don't block on API errors
  }
};